import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  Cloud,
  Shield,
  Globe,
  Zap,
  CheckCircle, 
  ArrowRight,
  Server,
  Database,
  Lock,
  Monitor,
  Smartphone,
  Users,
  BarChart3,
  Settings,
  RefreshCw,
  HardDrive,
  Wifi,
  FileText,
  Mail,
  Calendar,
  MessageSquare,
  Layers,
  Activity,
  TrendingUp,
  Clock
} from 'lucide-react';

const CloudServices: React.FC = () => {
  const cloudSolutions = [
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Microsoft Azure Cloud",
      description: "Comprehensive cloud infrastructure and platform services for scalable business solutions.",
      features: ["Virtual machines", "App services", "Storage solutions", "Network security"]
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Cloud Database Services",
      description: "Managed database services including Azure SQL, Cosmos DB, and data warehousing solutions.",
      features: ["Azure SQL Database", "Cosmos DB", "Data warehousing", "Backup & recovery"]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Cloud Security",
      description: "Enterprise-grade security solutions to protect your cloud infrastructure and data.",
      features: ["Identity management", "Threat protection", "Compliance monitoring", "Access control"]
    },
    {
      icon: <Monitor className="w-6 h-6" />,
      title: "Cloud Monitoring",
      description: "Real-time monitoring and analytics for optimal cloud performance and reliability.",
      features: ["Performance monitoring", "Log analytics", "Alert management", "Cost optimization"]
    }
  ];

  const azureServices = [
    {
      icon: <Server className="w-8 h-8" />,
      title: "Azure Virtual Machines",
      description: "Scalable computing power with Windows and Linux virtual machines in the cloud.",
      category: "Compute"
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Azure SQL Database",
      description: "Fully managed relational database service with built-in intelligence and security.",
      category: "Database"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Azure App Service",
      description: "Build and host web apps, mobile backends, and RESTful APIs in the cloud.",
      category: "Web & Mobile"
    },
    {
      icon: <HardDrive className="w-8 h-8" />,
      title: "Azure Storage",
      description: "Highly available, secure, and scalable cloud storage for any type of data.",
      category: "Storage"
    },
    {
      icon: <Wifi className="w-8 h-8" />,
      title: "Azure Virtual Network",
      description: "Secure private networks in the cloud with hybrid connectivity options.",
      category: "Networking"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Azure Analytics",
      description: "Advanced analytics and machine learning services for data-driven insights.",
      category: "Analytics"
    }
  ];

  const migrationProcess = [
    {
      step: "01",
      title: "Cloud Assessment",
      description: "Evaluate your current infrastructure and identify cloud migration opportunities.",
      color: "bg-blue-500"
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Develop comprehensive cloud migration strategy with timelines and resource allocation.",
      color: "bg-green-500"
    },
    {
      step: "03",
      title: "Architecture Design",
      description: "Design optimal cloud architecture for performance, security, and cost-effectiveness.",
      color: "bg-purple-500"
    },
    {
      step: "04",
      title: "Migration Execution",
      description: "Execute migration with minimal downtime using proven tools and methodologies.",
      color: "bg-orange-500"
    },
    {
      step: "05",
      title: "Testing & Optimization",
      description: "Comprehensive testing and performance optimization for cloud environments.",
      color: "bg-red-500"
    },
    {
      step: "06",
      title: "Ongoing Management",
      description: "Continuous monitoring, maintenance, and optimization of cloud resources.",
      color: "bg-indigo-500"
    }
  ];

  const benefits = [
    "Reduce IT infrastructure costs by up to 40%",
    "Scale resources up or down based on demand",
    "Improve business continuity and disaster recovery",
    "Enhance security with enterprise-grade protection",
    "Enable remote work and collaboration",
    "Accelerate innovation with cloud-native services"
  ];

  const cloudFeatures = [
    {
      title: "Scalability",
      description: "Automatically scale resources based on demand without infrastructure investments.",
      icon: <TrendingUp className="w-8 h-8" />
    },
    {
      title: "Security",
      description: "Enterprise-grade security with compliance certifications and threat protection.",
      icon: <Shield className="w-8 h-8" />
    },
    {
      title: "Reliability",
      description: "99.99% uptime SLA with built-in redundancy and disaster recovery capabilities.",
      icon: <CheckCircle className="w-8 h-8" />
    },
    {
      title: "Cost Efficiency",
      description: "Pay only for what you use with flexible pricing models and cost optimization tools.",
      icon: <BarChart3 className="w-8 h-8" />
    },
    {
      title: "Global Reach",
      description: "Deploy applications worldwide with Azure's global network of data centers.",
      icon: <Globe className="w-8 h-8" />
    },
    {
      title: "Innovation",
      description: "Access cutting-edge AI, ML, and IoT services to drive business innovation.",
      icon: <Zap className="w-8 h-8" />
    }
  ];

  const deploymentModels = [
    {
      title: "Public Cloud",
      description: "Fully managed cloud services with shared infrastructure and maximum cost efficiency.",
      features: ["Cost-effective", "Scalable", "Managed services", "Global availability"]
    },
    {
      title: "Private Cloud",
      description: "Dedicated cloud environment for enhanced security and compliance requirements.",
      features: ["Enhanced security", "Compliance ready", "Dedicated resources", "Custom configuration"]
    },
    {
      title: "Hybrid Cloud",
      description: "Combination of on-premises and cloud resources for optimal flexibility and control.",
      features: ["Flexible deployment", "Data sovereignty", "Legacy integration", "Gradual migration"]
    },
    {
      title: "Multi-Cloud",
      description: "Leverage multiple cloud providers for redundancy, compliance, and best-of-breed services.",
      features: ["Vendor independence", "Risk mitigation", "Best-of-breed", "Geographic distribution"]
    }
  ];

  return (
    <>
      <SEOHead
        title="Cloud Services | CRMONCE - Microsoft Azure Cloud Solutions"
        description="Professional Microsoft Azure cloud services including migration, infrastructure, security, and managed services. Transform your business with scalable cloud solutions."
        keywords="Microsoft Azure, cloud services, cloud migration, Azure infrastructure, cloud security, managed cloud services, hybrid cloud, Azure consulting"
        canonical="https://crmonce.com/cloud-services"
        ogImage="/images/cloud-services-og.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Microsoft Azure Cloud Services",
          "description": "Professional Microsoft Azure cloud services including migration, infrastructure management, and cloud security solutions.",
          "provider": {
            "@type": "Organization",
            "name": "CRMONCE",
            "url": "https://crmonce.com",
            "logo": "https://crmonce.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8096556344",
              "contactType": "sales",
              "email": "info@crmonce.com"
            }
          },
          "serviceType": "Cloud Services",
          "category": "Cloud Computing",
          "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Azure Cloud Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cloud Migration",
                  "description": "Migrate your infrastructure and applications to Microsoft Azure"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cloud Infrastructure",
                  "description": "Managed Azure infrastructure and platform services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cloud Security",
                  "description": "Enterprise-grade cloud security and compliance solutions"
                }
              }
            ]
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        {/* Hero Section */}
        <section className="pt-48 pb-20 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <Cloud className="w-12 h-12 mr-4" />
                <h1 className="text-5xl font-bold">Microsoft Azure Cloud Services</h1>
              </div>
              <p className="text-xl text-blue-100 mb-8">
                Transform your business with scalable, secure, and cost-effective Microsoft Azure cloud solutions. 
                Expert cloud migration, infrastructure management, and ongoing support services.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300"
              >
                Start Your Cloud Journey
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Cloud Solutions */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Cloud Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From cloud strategy to implementation and management, we provide end-to-end 
                Microsoft Azure cloud services tailored to your business needs.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {cloudSolutions.map((solution, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-blue-600 mb-4">{solution.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {solution.description}
                  </p>
                  <div className="space-y-2">
                    {solution.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Azure Services */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Microsoft Azure Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Leverage the full power of Microsoft Azure with our expert implementation 
                and management of core cloud services.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {azureServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-cyan-600 mb-4">{service.icon}</div>
                  <div className="text-sm text-cyan-600 font-medium mb-2">{service.category}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Cloud Features */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose Cloud Services?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience the transformative benefits of cloud computing with enterprise-grade 
                security, scalability, and innovation capabilities.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cloudFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className="text-blue-600 mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Migration Process */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Cloud Migration Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our proven methodology ensures smooth, secure, and efficient migration 
                to Microsoft Azure cloud platform.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {migrationProcess.map((process, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative"
                >
                  <div className={`absolute -top-4 -left-4 w-12 h-12 ${process.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Deployment Models */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Cloud Deployment Models
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose the right cloud deployment model that aligns with your business 
                requirements, security needs, and compliance standards.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {deploymentModels.map((model, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">{model.title}</h3>
                  <p className="text-gray-600 mb-6">{model.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {model.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Cloud Benefits for Your Business
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Transform your business operations with the power of Microsoft Azure cloud services.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center bg-white bg-opacity-10 rounded-lg p-6"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-gray-900 to-blue-900">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Move to the Cloud?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let our Azure experts help you design, implement, and manage your cloud infrastructure 
                for maximum performance, security, and cost-effectiveness.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Get Cloud Assessment
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  View Migration Guide
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CloudServices;
